import { useCallback } from 'react';
import { handleSystemErrorReport } from '@mb/lib/utils';
import flatten from 'lodash/flatten';
import { AppFiltersProps, FilterData } from '../types';
import {
  getUrlPageNumber,
  encodeFilterValue,
  decodeFilterValue,
  SWITCHES_FIELDS,
} from '../utils';
import { compareParam, isHomePage } from '../utils/helpers';

const FILTER_TEMPLATE = '[prefix].[field]=[value]';

interface FilterServiceProps {
  appFilters: AppFiltersProps;
}

const useFilterService = ({ appFilters = {} }: FilterServiceProps) => {
  const injectFilterData = (filter: FilterData, field: string) => {
    filter.field = field;
    filter.id = getFilterId(filter) || '';
  };

  const getFilterId = useCallback((filter: FilterData): string => {
    const id = parseFilter(filter);

    return Array.isArray(id) ? id.join('&') : id || '';
  }, []);

  const createFilter = (field: string, value: string, prefix = 'filter') => {
    const id = createFilterQuery(field, value, { prefix });
    return { id, field, value, label: value };
  };

  const parseFilter = (filter: FilterData) => {
    if (filter.low != null || filter.high != null) {
      return parseRangeFilter(filter);
    }
    return parseValueFilter(filter);
  };

  const createFilterQuery = useCallback(
    (field: string, value: string, config = {}) => {
      try {
        const { prefix, template } = Object.assign(
          { prefix: 'filter', template: FILTER_TEMPLATE },
          config,
        );

        const newValue = template
          ?.replace('[prefix]', prefix)
          ?.replace('[field]', field)
          // Function callback used for prices to avoid build-in functionality,
          // because if value is '$$', .replace method will return just "$".
          ?.replace('[value]', () => {
            return value;
          });
        return newValue;
      } catch (error) {
        console.error(error);
        handleSystemErrorReport(
          `Error in createFilterQuery: ${error}`,
          'createFilterQuery',
        );
        return '';
      }
    },
    [],
  );

  const parseValueFilter = (filter: FilterData) => {
    const isSwitch = SWITCHES_FIELDS.includes(filter?.field);
    return createFilterQuery(
      filter.field,
      isSwitch ? filter.value : filter.label,
    );
  };

  const parseRangeFilter = (filter: FilterData) => {
    const low = createFilterQuery(
      `${filter.field}.low`,
      String(filter.low) || '0',
    );
    const high = createFilterQuery(
      `${filter.field}.high`,
      String(filter.high) || '0',
    );

    return [low, high];
  };

  const getFiltersByField = useCallback(
    (field: string, prefix = 'filter', endString = '') => {
      return Object.values(appFilters).filter((filter) => {
        return filter?.id?.startsWith(`${prefix}.${field}${endString}`);
      });
    },
    [appFilters],
  );

  const getFilterById = (id: string) => {
    return appFilters[id];
  };

  const getFiltersByPropertyValue = useCallback(
    (
      property: keyof FilterData,
      value: string,
      config: { isStarting?: boolean; field?: string } = {},
      filtersList: Record<string, FilterData> = appFilters,
    ): FilterData[] => {
      const { isStarting = false, field = undefined } = config;

      return Object.values(filtersList).filter((filter) => {
        const filterValue = filter[property];
        let foundFilter = filterValue === value;

        if (isStarting && value?.length > 0) {
          if (Array.isArray(filterValue)) {
            foundFilter = filterValue.every((filter) =>
              filter.startsWith(value),
            );
          } else {
            foundFilter = String(filterValue).startsWith(value);
          }
        }

        if (field != undefined) {
          foundFilter = foundFilter && filter.field === field;
        }

        return foundFilter;
      });
    },
    [appFilters],
  );

  function createNewFilter(
    key: string,
    value: string | boolean,
    currentAppFilters = appFilters,
  ) {
    const decodedValue = decodeFilterValue(value);
    const id = `${key}=${decodedValue}`;
    const currentFilters = flatten(
      Object.values(currentAppFilters).map(parseFilter),
    );

    if (!currentFilters.includes(id)) {
      //for each filter there, add a filter in the app, as if chosen by the user
      const category = key.split('.')[1];
      return {
        id,
        field: category,
        label: decodedValue,
        value: decodedValue,
      };
    } else {
      return null;
    }
  }

  function getNewFilters(
    key: string,
    value: string | string[],
    currentAppFilters = appFilters,
  ) {
    const newFilters = [];
    let newFilter = null;
    if (Array.isArray(value)) {
      for (const val of value) {
        newFilter = createNewFilter(key, val, currentAppFilters);
        if (newFilter != null) {
          newFilters.push(newFilter);
        }
      }
    } else {
      newFilter = createNewFilter(key, value, currentAppFilters);
      if (newFilter != null) {
        newFilters.push(newFilter);
      }
    }

    if (newFilters.length > 0) {
      return newFilters;
    }
    return [];
  }

  const encodeFilterId = (filterId: string) => {
    if (filterId == null) return;
    const adjustedId = filterId?.replace('.', ':')?.replace('=', ':');
    const [prefix, field, value] = adjustedId?.split(':');
    const encodedValue = encodeFilterValue(value);
    return [prefix, field, encodedValue].join(':');
  };

  const getUrlWithoutFilters = () => {
    const hashSearch = window.location.hash.split('?')[1] || '';
    const urlParams = new URLSearchParams(
      `${window.location.search}&${hashSearch}`,
    );
    const utmUrlParamNames = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
    ];
    const allowedUrlParamNames = [
      'q',
      'material',
      'from',
      compareParam,
      ...utmUrlParamNames,
    ];

    const url = new URL(window.location.origin + window.location.pathname);

    allowedUrlParamNames.forEach((paramName) => {
      const paramValue = urlParams.get(paramName);
      if (paramValue != null) {
        url.searchParams.set(paramName, paramValue);
      }
    });

    return url.href;
  };

  const setFiltersToUrl = (
    filtersIds: string[],
    searchToggle: string | null = null,
  ) => {
    if (isHomePage()) {
      return;
    }

    let url = getUrlWithoutFilters();

    for (const filterId of filtersIds) {
      const encodedFilterId = encodeFilterId(filterId);
      url += `#/${encodedFilterId}`;
    }

    if (searchToggle != null) {
      url += `#/view:${searchToggle}`;
    }

    const initialPageNumber = getUrlPageNumber();
    if (initialPageNumber != null) {
      const optionalHash = url.slice(-1) === '#' ? '/' : '#/';
      url += `${optionalHash}page:${initialPageNumber}`;
    }

    if (window.location.href !== url) {
      window.history.pushState({ path: url }, '', url);
    }
  };

  return {
    getFiltersByField,
    getFilterById,
    getFiltersByPropertyValue,
    injectFilterData,
    getFilterId,
    createFilter,
    parseFilter,
    createFilterQuery,
    parseValueFilter,
    parseRangeFilter,
    getUrlWithoutFilters,
    setFiltersToUrl,
    encodeFilterId,
    createNewFilter,
    getNewFilters,
  };
};

export { useFilterService };
